// The display-facet vocabulary and the readers that turn schedule rows into
// filter options and row labels. Schedule types stay type-only.
import type { EHRAppointment, ScheduleItem } from '@heidi/integrations/ehr/contracts';

import type { DisplayFacet, ScheduleItemWithDisplayFacets } from '../session-list/types';
import type { ScheduleFilterOption } from './scheduleFilterOptions';

export const DISPLAY_FACET_PROVIDER_GROUP = 'provider_group';
export const DISPLAY_FACET_DEPARTMENT = 'department';

export type ScheduleFacetOptions = Record<string, ScheduleFilterOption[]>;

/** Reads display facets from a schedule row. Absent/null means no facets. */
export function getScheduleItemDisplayFacets(
  item: EHRAppointment | ScheduleItem
): readonly DisplayFacet[] {
  if ('record_id' in item && 'patient_id' in item) return [];

  const row = item as ScheduleItemWithDisplayFacets;
  const facets = row.appointment?.display_facets;
  if (facets == null || facets.length === 0) return [];

  return facets;
}

function isRowDisplayFacet(facet: DisplayFacet): boolean {
  return facet.show_in_row === true;
}

/** Facets marked for row rendering on this appointment. */
export function getRowDisplayFacets(
  facets: readonly DisplayFacet[]
): readonly DisplayFacet[] {
  return facets.filter(isRowDisplayFacet);
}

function uniqueFilterOptions(items: ScheduleFilterOption[]): ScheduleFilterOption[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

/** Distinct dropdown options for a facet from integration schedule rows. */
export function deriveDisplayFacetsFromSchedule(
  schedule: readonly ScheduleItemWithDisplayFacets[],
  facetKey: string
): ScheduleFilterOption[] {
  const options = schedule
    .flatMap((row) => getScheduleItemDisplayFacets(row))
    .filter((facet) => facet.facet === facetKey)
    .map((facet) => ({
      id: facet.value,
      label: facet.display?.trim() || facet.value,
    }));

  return uniqueFilterOptions(options).sort((a, b) => a.label.localeCompare(b.label));
}

/** Derives all integration-provided facet options in one pass for query selection. */
export function deriveScheduleFacetOptions(
  schedule: readonly ScheduleItemWithDisplayFacets[]
): ScheduleFacetOptions {
  const optionsByFacet: ScheduleFacetOptions = {};
  const seenValuesByFacet = new Map<string, Set<string>>();

  for (const row of schedule) {
    for (const facet of getScheduleItemDisplayFacets(row)) {
      const seenValues = seenValuesByFacet.get(facet.facet) ?? new Set<string>();
      if (seenValues.has(facet.value)) continue;

      seenValues.add(facet.value);
      seenValuesByFacet.set(facet.facet, seenValues);
      (optionsByFacet[facet.facet] ??= []).push({
        id: facet.value,
        label: facet.display?.trim() || facet.value,
      });
    }
  }

  for (const options of Object.values(optionsByFacet)) {
    options.sort((a, b) => a.label.localeCompare(b.label));
  }

  return optionsByFacet;
}

export function scheduleHasDisplayFacets(
  schedule: readonly (EHRAppointment | ScheduleItem)[]
): boolean {
  return schedule.some((row) => getScheduleItemDisplayFacets(row).length > 0);
}

function findRowDisplayFacet(
  facets: readonly DisplayFacet[] | null | undefined,
  facetKey: string
): DisplayFacet | undefined {
  return facets?.find((facet) => facet.facet === facetKey && isRowDisplayFacet(facet));
}

/** Human label for a row-display facet — prefers `display`, falls back to `value`. */
export function getDisplayFacetLabel(
  facets: readonly DisplayFacet[] | null | undefined,
  facetKey: string
): string | null {
  const facet = findRowDisplayFacet(facets, facetKey);
  if (!facet) return null;
  return facet.display?.trim() || facet.value;
}

/** Unique facet keys with `show_in_row: true`, in first-seen order across the schedule. */
export function collectRowDisplayFacetKeys(
  schedule: readonly (EHRAppointment | ScheduleItem)[]
): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();

  for (const row of schedule) {
    for (const facet of getRowDisplayFacets(getScheduleItemDisplayFacets(row))) {
      if (seen.has(facet.facet)) continue;
      seen.add(facet.facet);
      keys.push(facet.facet);
    }
  }

  return keys;
}

export function scheduleHasRowDisplayFacets(
  schedule: readonly (EHRAppointment | ScheduleItem)[]
): boolean {
  return collectRowDisplayFacetKeys(schedule).length > 0;
}
